/**
 * Ubicación de los reportes de un viaje sobre la traza dibujada.
 *
 * Vive fuera del componente del mapa porque lo usan dos lados: la línea de
 * tiempo del reproductor (qué hora era y a cuánto iba a los tantos metros) y el
 * coloreo de la traza por velocidad.
 */

/**
 * Al ubicar cada reporte sobre la traza, cuántos metros de distancia a la traza
 * vale cada metro de salto hacia adelante: con 0.05, un lugar 1 km más adelante
 * tiene que quedar 50 m más cerca para ganarle al actual. Evita que un reporte
 * de la ida se pegue a la vuelta cuando el vehículo pasa dos veces por la misma
 * calle.
 */
const PENALIZACION_SALTO_TRAZA = 0.05;

/** Tope de velocidad (200 km/h en m/s) para lo que se puede saltar entre reportes. */
const VELOCIDAD_MAXIMA_TRAZA_MS = 200 / 3.6;

/** Salto mínimo permitido entre dos reportes, por si llegan casi juntos. */
const VENTANA_MINIMA_TRAZA_M = 1000;

/** Un reporte del viaje: dónde y cuándo (epoch ms). */
export interface ReporteEnTraza {
  coord: [number, number];
  t: number;
}

/**
 * Metros recorridos sobre la traza hasta cada reporte, en el mismo orden que
 * `puntos`, más los metros hasta cada vértice de la traza.
 *
 * La traza dibujada tiene sus propios vértices (el snap a calle agrega puntos
 * en las curvas y la ida y la vuelta van corridas a los costados), así que a
 * cada reporte hay que encontrarle su lugar sobre ella: se lo proyecta, en
 * orden y siempre hacia adelante del reporte anterior.
 *
 * Hacia adelante no alcanza con "el punto más cercano": la traza puede volver
 * a pasar por el mismo lugar más tarde, y un reporte de la ida terminaría
 * pegado a la vuelta. Por eso cada metro de salto cuesta
 * (`PENALIZACION_SALTO_TRAZA`): un lugar más adelante gana sólo si queda
 * claramente más cerca. Además el salto no puede ser mayor a lo que el
 * vehículo pudo recorrer entre los dos reportes.
 *
 * Antes se hacía al revés (cada vértice elegía entre el reporte actual y el
 * siguiente) y con el GPS saltando unos metros mientras estaba estacionado,
 * el siguiente podía no quedar nunca más cerca: el algoritmo se trababa en el
 * primer reporte y media traza quedaba con la hora del arranque.
 */
export function ubicarReportesEnTraza(
  coords: [number, number][],
  puntos: ReporteEnTraza[],
  distancia: (a: [number, number], b: [number, number]) => number,
): { hastaVertice: number[]; distancias: number[] } {
  // Metros recorridos hasta cada vértice.
  const hastaVertice = new Array<number>(coords.length);
  hastaVertice[0] = 0;
  for (let j = 1; j < coords.length; j++) {
    hastaVertice[j] = hastaVertice[j - 1] + distancia(coords[j - 1], coords[j]);
  }

  const distancias: number[] = [];
  if (coords.length < 2) return { hastaVertice, distancias };

  // Dónde quedó el reporte anterior: tramo y metros recorridos.
  let tramo = 0;
  let posicion = 0;
  let fechaAnterior = puntos[0]?.t ?? 0;

  for (const punto of puntos) {
    const segundos = Math.max(0, (punto.t - fechaAnterior) / 1000);
    fechaAnterior = punto.t;
    const alcance =
      posicion +
      Math.max(VENTANA_MINIMA_TRAZA_M, segundos * VELOCIDAD_MAXIMA_TRAZA_MS);

    // Plano local en metros alrededor del reporte: a estas distancias la
    // curvatura no cuenta y proyectar sobre un segmento es geometría plana.
    const [lng0, lat0] = punto.coord;
    const kx = Math.cos((lat0 * Math.PI) / 180) * 111320;
    const ky = 110574;

    let mejor = { costo: Infinity, tramo, d: posicion };

    for (let s = tramo; s < coords.length - 1; s++) {
      const salto = Math.max(0, hastaVertice[s] - posicion);
      // Más adelante el salto sólo crece: nada puede mejorar lo que ya hay.
      if (PENALIZACION_SALTO_TRAZA * salto >= mejor.costo) break;
      if (hastaVertice[s] > alcance) break;

      const ax = (coords[s][0] - lng0) * kx;
      const ay = (coords[s][1] - lat0) * ky;
      const bx = (coords[s + 1][0] - lng0) * kx;
      const by = (coords[s + 1][1] - lat0) * ky;
      const dx = bx - ax;
      const dy = by - ay;
      const largo2 = dx * dx + dy * dy;
      let t = largo2
        ? Math.min(1, Math.max(0, -(ax * dx + ay * dy) / largo2))
        : 0;

      const largo = hastaVertice[s + 1] - hastaVertice[s];
      // En el tramo del reporte anterior, no volver atrás de él.
      if (s === tramo && largo > 0) {
        t = Math.max(t, (posicion - hastaVertice[s]) / largo);
      }

      const d = hastaVertice[s] + t * largo;
      const costo =
        Math.hypot(ax + t * dx, ay + t * dy) +
        PENALIZACION_SALTO_TRAZA * (d - posicion);
      if (costo < mejor.costo) mejor = { costo, tramo: s, d };
    }

    tramo = mejor.tramo;
    posicion = mejor.d;
    distancias.push(posicion);
  }

  return { hastaVertice, distancias };
}

/**
 * Valor `v` a esa distancia, interpolado entre los dos elementos de `lista`
 * (ordenada por `d`) que la rodean. Fuera de la lista, el del extremo.
 */
export function valorInterpolado(
  lista: { d: number; v: number }[],
  distancia: number,
): number | undefined {
  if (!lista.length) return undefined;
  if (distancia <= lista[0].d) return lista[0].v;
  const ultimo = lista.length - 1;
  if (distancia >= lista[ultimo].d) return lista[ultimo].v;

  let bajo = 0;
  let alto = ultimo;
  while (alto - bajo > 1) {
    const medio = (bajo + alto) >> 1;
    if (lista[medio].d <= distancia) bajo = medio;
    else alto = medio;
  }

  const tramo = lista[alto].d - lista[bajo].d;
  if (tramo <= 0) return lista[alto].v;
  const avance = (distancia - lista[bajo].d) / tramo;
  return lista[bajo].v + avance * (lista[alto].v - lista[bajo].v);
}
